// src/pages/ServicesPage.js
import React, { useState, useEffect } from 'react';
import { servicesAPI } from '../services/api';

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [currentService, setCurrentService] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    rate_per_hour: ''
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const response = await servicesAPI.getAll();
      setServices(response.data.services);
      setError('');
    } catch (err) {
      setError('Fehler beim Laden der Dienstleistungen');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const value = e.target.name === 'rate_per_hour'
      ? e.target.value.replace(',', '.') // Komma durch Punkt ersetzen
      : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (currentService) {
        // Dienstleistung aktualisieren
        await servicesAPI.update(currentService.id, formData);
        alert('Dienstleistung erfolgreich aktualisiert');
      } else {
        // Neue Dienstleistung erstellen
        await servicesAPI.create(formData);
        alert('Dienstleistung erfolgreich erstellt');
      }
      
      // Dienstleistungen neu laden
      await fetchServices();
      
      // Formular zurücksetzen
      resetForm();
    } catch (err) {
      setError(currentService 
        ? 'Fehler beim Aktualisieren der Dienstleistung' 
        : 'Fehler beim Erstellen der Dienstleistung');
      console.error(err);
    }
  };

  const handleEdit = (service) => {
    setCurrentService(service);
    setFormData({
      name: service.name,
      description: service.description || '',
      rate_per_hour: service.rate_per_hour
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (serviceId) => {
    if (window.confirm('Möchten Sie diese Dienstleistung wirklich löschen?')) {
      try {
        await servicesAPI.delete(serviceId);
        await fetchServices();
        alert('Dienstleistung erfolgreich gelöscht');
      } catch (err) {
        if (err.response && err.response.status === 400) {
          alert(err.response.data.error);
        } else {
          setError('Fehler beim Löschen der Dienstleistung');
          console.error(err);
        }
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      rate_per_hour: ''
    });
    setCurrentService(null);
    setShowForm(false);
  };

  // Format currency
  const formatCurrency = (amount) => {
    return parseFloat(amount).toFixed(2).replace('.', ',');
  };

  if (loading && services.length === 0) {
    return <div className="text-center py-10">Lade Dienstleistungen...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Dienstleistungsmanagement</h1>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded"
        >
          {showForm ? 'Abbrechen' : 'Neue Dienstleistung'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {showForm && (
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <h2 className="text-xl font-medium mb-4">
            {currentService ? 'Dienstleistung bearbeiten' : 'Neue Dienstleistung'}
          </h2>
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-gray-700 mb-2">Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700 mb-2">Beschreibung</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                rows="3"
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700 mb-2">Stundenansatz (CHF) *</label>
              <input
                type="text"
                name="rate_per_hour"
                value={formData.rate_per_hour}
                onChange={handleChange}
                required
                pattern="[0-9]+(\.[0-9]{1,2})?"
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded"
              >
                {currentService ? 'Aktualisieren' : 'Speichern'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-medium mb-4">Dienstleistungsliste</h2>
        
        {services.length === 0 ? (
          <p className="text-gray-500">Keine Dienstleistungen vorhanden</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Beschreibung</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stundenansatz</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Aktionen</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {services.map((service) => (
                  <tr key={service.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{service.name}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{service.description || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">CHF {formatCurrency(service.rate_per_hour)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(service)}
                        className="text-primary-600 hover:text-primary-900 mr-3"
                      >
                        Bearbeiten
                      </button>
                      <button
                        onClick={() => handleDelete(service.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Löschen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServicesPage;